import React from "react";
import { BookOpen, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShareButtons } from "../components/ShareButtons";

const FocusBigIdeaPage = () => {
  return (
    <div style={{ background: '#FBF8F3', minHeight: '100vh' }}>
      <Helmet>
        <title>Are You Paying Attention to What Truly Matters? | The Reading Room</title>
        <meta name="description" content="One Book, One Big Idea — inspired by Focus: The Hidden Driver of Excellence by Daniel Goleman." />
      </Helmet>

      {/* Minimal Header */}
      <header className="py-4 px-5 md:px-10" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" style={{ color: '#7A2E2E' }} strokeWidth={1.5} />
            <span className="text-sm font-bold tracking-wider" style={{ fontFamily: "'Playfair Display', serif", color: '#2C1810' }}>THE READING ROOM</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-xs font-medium transition-opacity hover:opacity-70" style={{ color: '#7A2E2E' }}>
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <article className="px-5 md:px-10 py-10 md:py-16">
        <div className="max-w-3xl mx-auto">

          {/* Section label */}
          <p className="uppercase tracking-[0.2em] text-[11px] font-bold mb-6" style={{ color: '#7A2E2E' }}>
            One Book, One Big Idea
          </p>

          {/* Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-[44px] font-bold leading-[1.12] mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C1810' }}>
            Are You Paying Attention to What Truly Matters?
          </h1>

          {/* Book attribution */}
          <p className="text-[15px] mb-10 leading-relaxed" style={{ color: '#6B5E53' }}>
            Inspired by <em>Focus: The Hidden Driver of Excellence</em> — Daniel Goleman
          </p>

          {/* Decorative divider */}
          <div className="w-16 h-[2px] rounded-full mb-10" style={{ background: '#D9A441' }} />

          {/* Book cover + intro */}
          <div className="flex flex-col sm:flex-row gap-6 mb-10">
            <img
              src="/covers/focus-the-hidden-driver-of-excellence.jpg"
              alt="Focus: The Hidden Driver of Excellence by Daniel Goleman"
              className="w-28 sm:w-32 h-auto object-contain rounded-lg shadow-md flex-shrink-0"
            />
            <div>
              <p className="text-[16px] leading-[1.8] mb-4" style={{ color: '#3A3229' }}>
                We live in a world where information is abundant, but attention is increasingly scarce.
              </p>
              <p className="text-[16px] leading-[1.8]" style={{ color: '#3A3229' }}>
                In <em>Focus</em>, Daniel Goleman reminds us that attention is more than concentration. It shapes how we understand ourselves, connect with others and see the world around us.
              </p>
            </div>
          </div>

          {/* Three dimensions */}
          <p className="text-[16px] leading-[1.8] mb-5" style={{ color: '#3A3229' }}>
            He explores three dimensions of attention:
          </p>
          <div className="space-y-4 mb-10 pl-1">
            {[
              { title: "Inner Focus", desc: "Understanding our thoughts, emotions and what truly matters to us." },
              { title: "Other Focus", desc: "Listening with empathy and giving people our genuine attention." },
              { title: "Outer Focus", desc: "Looking beyond immediate concerns to understand the bigger picture." },
            ].map((item) => (
              <div key={item.title} className="flex gap-3 items-start">
                <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: '#7A2E2E' }} />
                <p className="text-[16px] leading-[1.7]" style={{ color: '#3A3229' }}>
                  <strong style={{ color: '#2C1810' }}>{item.title}</strong> — {item.desc}
                </p>
              </div>
            ))}
          </div>

          {/* The One Big Idea */}
          <div className="rounded-xl p-6 md:p-8 mb-10" style={{ background: '#F0EBE1', border: '1px solid rgba(0,0,0,0.06)' }}>
            <p className="uppercase tracking-[0.15em] text-[10px] font-bold mb-4" style={{ color: '#7A2E2E' }}>
              The One Big Idea
            </p>
            <p className="text-lg md:text-xl font-semibold leading-snug mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C1810' }}>
              In a world full of distractions, the real art of focus is knowing what deserves our attention—and what does not.
            </p>
            <p className="text-[15px] leading-[1.75]" style={{ color: '#3A3229' }}>
              We often measure our days by how much we accomplish. But perhaps a more meaningful measure is where we direct our mental energy.
            </p>
            <p className="text-[15px] leading-[1.75] mt-3" style={{ color: '#3A3229' }}>
              A conversation, a relationship, a difficult decision or a quiet moment of reflection—each becomes richer when we are fully present.
            </p>
          </div>

          {/* Reflection question */}
          <div className="text-center py-8 mb-8">
            <p className="uppercase tracking-[0.15em] text-[10px] font-bold mb-4" style={{ color: '#D9A441' }}>
              A Question to Reflect On
            </p>
            <p className="text-xl md:text-2xl font-semibold leading-snug italic max-w-lg mx-auto" style={{ fontFamily: "'Playfair Display', serif", color: '#2C1810' }}>
              What received most of your attention today—and was it truly worth it?
            </p>
          </div>

          {/* Divider */}
          <div className="w-16 h-[2px] rounded-full mx-auto mb-8" style={{ background: '#D9A441' }} />

          {/* View in library link */}
          <div className="text-center">
            <Link
              to="/book/book_0032"
              className="inline-flex items-center gap-2 text-sm font-semibold transition-opacity hover:opacity-70"
              style={{ color: '#7A2E2E' }}
            >
              View Focus in the Library <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
            </Link>
          </div>

          <ShareButtons
            title="Are You Paying Attention to What Truly Matters?"
            excerpt="One Book, One Big Idea — inspired by Focus: The Hidden Driver of Excellence by Daniel Goleman. In a world full of distractions, what we choose to focus on shapes how we think, connect and grow."
            url="https://thereading-room.com/reflection/focus-big-idea"
            label="Share this idea:"
            testIdPrefix="big-idea-share"
          />
        </div>
      </article>

      {/* Minimal footer */}
      <footer className="py-6 px-5 text-center" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
        <p className="text-[11px]" style={{ color: '#8A7E72' }}>
          The Reading Room &middot; Books. Reflections. Growth. &middot; {new Date().getFullYear()}
        </p>
      </footer>
    </div>
  );
};

export default FocusBigIdeaPage;
